// Module 16(b) §8 — job deletion: confirm → delete → reload.
//
// Ports `DataExportTable.vue:302-311`, which deleted with NO confirmation at all and swallowed the
// failure into a `console.error`, leaving the deleted-looking row on screen. Contract §14 makes
// the confirm mandatory and the failure visible: this flow returns an outcome and the caller owns
// the copy.
//
// `confirm` and `translate` are injected ports (the `ThreadMutationFlow` idiom) so the whole flow
// runs headless in a spec without mounting `<ConfirmHost/>`.
import type { ExportJob } from '../models/exportJob.types'
import { exportJobService, type ExportJobService } from '../service/exportJobService'
import { useDataExportStore, type DataExportStore } from '../stores/dataExportStore'

import { useAccount, useWorkspace } from '@/features/account'
import { ApiError } from '@/shared/http'
import { useI18n } from '@/shared/i18n'
import { useConfirm, type ConfirmOptions } from '@/shared/ui'

type ConfirmPort = (options: ConfirmOptions) => Promise<boolean>
type Translate = (key: string) => string

export type DeleteExportJobOutcome =
  | { status: 'deleted' }
  | { status: 'canceled' }
  | { status: 'missingScope' }
  | { status: 'failed'; error: ApiError }

export interface DeleteExportJobFlowDeps {
  store?: DataExportStore
  service?: ExportJobService
  confirm?: ConfirmPort
  translate?: Translate
}

export class DeleteExportJobFlow {
  private readonly store: DataExportStore
  private readonly service: ExportJobService
  private readonly confirm: ConfirmPort
  private readonly translate: Translate

  constructor(deps: DeleteExportJobFlowDeps = {}) {
    this.store = deps.store ?? useDataExportStore()
    this.service = deps.service ?? exportJobService
    this.confirm = deps.confirm ?? useConfirm()
    if (deps.translate) this.translate = deps.translate
    else {
      const { t } = useI18n()
      this.translate = (key) => t(key)
    }
  }

  async execute(job: Pick<ExportJob, 'export_job_group_id'>): Promise<DeleteExportJobOutcome> {
    // The delete route is keyed by the GROUP id, not `common_fields.id` — a job without one cannot
    // be addressed, and legacy would have built a URL ending in `/undefined`.
    const groupId = job.export_job_group_id
    if (!groupId) return { status: 'missingScope' }

    const confirmed = await this.confirm({
      message: this.translate('dataExport.list.confirmDelete'),
      title: this.translate('dataExport.list.confirmDeleteTitle'),
      confirmLabel: this.translate('common.delete'),
      cancelLabel: this.translate('common.cancel'),
      danger: true,
    })
    if (!confirmed) return { status: 'canceled' }

    const { accountUserId } = useAccount()
    const { workspaceId } = useWorkspace()
    if (accountUserId.value === undefined || workspaceId.value === undefined) {
      return { status: 'missingScope' }
    }

    try {
      await this.service.remove(accountUserId.value, workspaceId.value, groupId)
      await this.store.loadJobs()
      return { status: 'deleted' }
    } catch (cause) {
      return {
        status: 'failed',
        error: ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause),
      }
    }
  }
}
